/**
 * Returns '#000' or '#fff' depending on which gives better contrast
 * against the given hex background color (WCAG perceived luminance).
 */
export const getContrastColor = (hex: string): string => {
  const h = (hex || '#6c757d').replace('#', '');
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.55 ? '#000' : '#fff';
};

// ---------------------------------------------------------------------------
// Tabler UI palette — use these with getContrastColor() for consistent badges.
// ---------------------------------------------------------------------------
export const COLOR_PRIMARY   = '#2563eb';
export const COLOR_SUCCESS   = '#2fb344';
export const COLOR_DANGER    = '#d63939';
export const COLOR_WARNING   = '#f76707';
export const COLOR_INFO      = '#4299e1';
export const COLOR_SECONDARY = '#6c757d';
export const COLOR_DARK      = '#1e293b';
// Aliases matching Tabler's bg-green / bg-blue / bg-red CSS classes:
export const COLOR_GREEN     = COLOR_SUCCESS;
export const COLOR_BLUE      = COLOR_INFO;
export const COLOR_RED       = COLOR_DANGER;

/**
 * Derives a stable hex color from any string (e.g. a category name).
 * Uses a simple djb2-style hash mapped to a fixed palette of muted Tabler-compatible hues.
 */
const STRING_COLOR_PALETTE: string[] = [
  '#206bc4', // blue
  '#2fb344', // green
  '#f76707', // orange
  '#ae3ec9', // purple
  '#d63939', // red
  '#0ca678', // teal
  '#f59f00', // yellow
  '#4263eb', // indigo
  '#e64980', // pink
  '#1098ad', // cyan
];

export const stringToColor = (str: string): string => {
  if (!str) return COLOR_SECONDARY;
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash) ^ str.charCodeAt(i);
  }
  return STRING_COLOR_PALETTE[Math.abs(hash) % STRING_COLOR_PALETTE.length];
};

/**
 * Converts HSL values to a hex color string.
 */
const hslToHex = (h: number, s: number, l: number): string => {
  const sn = s / 100;
  const ln = l / 100;
  const a = sn * Math.min(ln, 1 - ln);
  const f = (n: number): string => {
    const k = (n + h / 30) % 12;
    const color = ln - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color).toString(16).padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
};

/**
 * Generates a deterministic color that adapts to light/dark mode by adjusting
 * lightness relative to the seed string.
 */
export const adaptiveColor = (str: string, isDark = false): string => {
  if (!str) return COLOR_SECONDARY;
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const h = Math.abs(hash) % 360;
  const s = 55 + (Math.abs(hash >> 4) % 25);
  const l = isDark ? 55 + (Math.abs(hash >> 8) % 15) : 35 + (Math.abs(hash >> 8) % 20);
  return hslToHex(h, s, l);
};

/**
 * Generates a vivid but readable random accent color for user-created entities.
 */
export const generateRandomColor = (): string => {
  const hue = Math.floor(Math.random() * 360);
  const saturation = 60 + Math.floor(Math.random() * 20);
  const lightness = 42 + Math.floor(Math.random() * 12);
  return hslToHex(hue, saturation, lightness);
};
